import { required } from "@vuelidate/validators";
import type { Ref } from "vue";
import { provide, computed } from "vue";
import type {
  Scenario,
  Equipment,
  OperatingParameters,
  WellProperties,
  XrefCables,
  XrefHousings,
  XrefIntakes,
  XrefPumps,
  XrefSeals
} from "@/composables/forms/formTypes";
import useForm from "@/composables/forms/useForm";
import { omit } from "@/utils/utils";
import { v4 } from "uuid";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import useDestructureScenario, {
  type DestructuredScenario,
  type XrefYToolBypassTubings,
  type XrefYToolEquipmentTubings
} from "@/composables/useDestructureScenario";
import { useScenarioValidator } from "@/composables/validation/scenarioValidator";
import { useAutomaticBoltOnDischarge } from "@/composables/equipment/boltOnDischarges/useAutomaticBoltOnDischarge";
import {
  filterEquipmentMapByOrder,
  filterHousingsByPumpOrder,
  createFilteredEquipmentObject,
  createStackAwareParameters,
  createStackAwareWellProperties,
  getYToolProperties
} from "./stackFiltering";
import {
  cablesFormKey,
  correlationsFormKey,
  deviationSurveyFormKey,
  equipmentFormKey,
  housingsFormKey,
  intakesFormKey,
  parametersFormKey,
  pumpsFormKey,
  scenarioCalculationsKey,
  scenarioKey,
  scenarioValidatorKey,
  sealsFormKey,
  wellPropertiesFormKey,
  scenarioFormKey,
  parametersKey,
  wellPropertiesKey,
  equipmentKey,
  deviationSurveyKey,
  sealsMapKey,
  intakesMapKey,
  cablesMapKey,
  pumpsMapKey,
  housingsMapKey,
  yToolBypassTubingsFormKey,
  yToolBypassTubingsMapKey,
  yToolEquipmentTubingsFormKey,
  yToolEquipmentTubingsMapKey,
  correlationsKey,
  upperFormsKey,
  lowerFormsKey
} from "./scenarioDetailProviderKeys";

export const useScenarioDetailProvider = (
  scenario: Ref<Scenario | undefined>
) => {
  // Computed values
  const {
    equipment,
    parameters,
    wellProperties,
    deviationSurvey,
    correlations,
    seals,
    intakes,
    cables,
    pumps,
    housings,
    yToolBypassTubings,
    yToolEquipmentTubings
  } = useDestructureScenario(scenario);

  // Form values
  const scenarioValidationRules = {
    title: { required },
    reason: { required }
  };

  const scenarioForm = useForm(scenario, scenarioValidationRules);
  const equipmentForm = useForm(equipment);
  const parametersForm = useForm(parameters);
  const wellPropertiesForm = useForm(wellProperties);
  const deviationSurveyForm = useForm(deviationSurvey);
  const sealsForm = useForm(seals);
  const intakesForm = useForm(intakes);
  const cablesForm = useForm(cables);
  const pumpsForm = useForm(pumps);
  const housingsForm = useForm(housings);
  const yToolBypassTubingsForm = useForm(yToolBypassTubings);
  const yToolEquipmentTubingsForm = useForm(yToolEquipmentTubings);
  const correlationsForm = useForm(correlations);
  const allForms = {
    correlations: correlationsForm.data,
    equipment: equipmentForm.data,
    parameters: parametersForm.data,
    wellProperties: wellPropertiesForm.data,
    deviationSurvey: deviationSurveyForm.data,
    seals: sealsForm.data,
    intakes: intakesForm.data,
    cables: cablesForm.data,
    pumps: pumpsForm.data,
    housings: housingsForm.data,
    yToolBypassTubings: yToolBypassTubingsForm.data,
    yToolEquipmentTubings: yToolEquipmentTubingsForm.data,
    scenario: scenarioForm.data
  };

  const scenarioCalculations = useScenarioCalculations(allForms);

  useAutomaticBoltOnDischarge({
    equipmentForm,
    pumpsForm,
    scenarioForm,
    wellPropertiesForm,
    yToolEquipmentTubingsForm,
    bottomTubingOuterDiameterInches:
      scenarioCalculations.tubingOuterDiameterAtPumpDischargeIn
  });

  const scenarioValidator = useScenarioValidator(
    allForms,
    scenarioCalculations
  );

  // Unfiltered forms: contains all original form data (used for calculations)
  // const unfilteredForms = computed<DestructuredScenario>(() => ({
  //   scenario: scenarioForm.data,
  //   correlations: correlationsForm.data,
  //   equipment: equipmentForm.data,
  //   parameters: parametersForm.data,
  //   wellProperties: wellPropertiesForm.data,
  //   deviationSurvey: deviationSurveyForm.data,
  //   seals: sealsForm.data,
  //   intakes: intakesForm.data,
  //   cables: cablesForm.data,
  //   pumps: pumpsForm.data,
  //   housings: housingsForm.data,
  //   yToolBypassTubings: yToolBypassTubingsForm.data,
  //   yToolEquipmentTubings: yToolEquipmentTubingsForm.data
  // }));

  // Upper forms: essentially the current default (no filtering needed)
  const upperForms = computed<DestructuredScenario>(() => ({
    scenario: scenarioForm.data,
    correlations: correlationsForm.data,
    equipment: equipmentForm.data,
    parameters: parametersForm.data,
    wellProperties: wellPropertiesForm.data,
    deviationSurvey: deviationSurveyForm.data,
    seals: sealsForm.data,
    intakes: intakesForm.data,
    cables: cablesForm.data,
    pumps: pumpsForm.data,
    housings: housingsForm.data,
    yToolBypassTubings: yToolBypassTubingsForm.data,
    yToolEquipmentTubings: yToolEquipmentTubingsForm.data
  }));

  // Lower forms: filtered for lower stack
  const lowerEquipment = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    return createFilteredEquipmentObject(equipmentValue, "lower");
  });

  const lowerParameters = computed(() => {
    return createStackAwareParameters(parametersForm.data.value, "lower");
  });

  const lowerWellProperties = computed(() => {
    return createStackAwareWellProperties(
      wellPropertiesForm.data.value,
      "lower"
    );
  });

  // Filter equipment maps by lower stack order arrays
  const lowerPumps = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    const lowerPumpsOrder =
      (equipmentValue?.lower_pumps_order as string[] | undefined) || [];
    return filterEquipmentMapByOrder(pumpsForm.data.value, lowerPumpsOrder);
  });

  const lowerIntakes = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    const lowerIntakesOrder =
      (equipmentValue?.lower_intakes_order as string[] | undefined) || [];
    return filterEquipmentMapByOrder(intakesForm.data.value, lowerIntakesOrder);
  });

  const lowerSeals = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    const lowerSealsOrder =
      (equipmentValue?.lower_seals_order as string[] | undefined) || [];
    return filterEquipmentMapByOrder(sealsForm.data.value, lowerSealsOrder);
  });

  const lowerCables = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    const lowerCablesOrder =
      (equipmentValue?.lower_cables_order as string[] | undefined) || [];
    return filterEquipmentMapByOrder(cablesForm.data.value, lowerCablesOrder);
  });

  // Filter housings: only include housings from filtered pumps
  // This ensures housings are correctly linked to lower stack pumps via scenario_equipment_xref_pump_id
  const lowerHousings = computed(() => {
    const equipmentValue = equipmentForm.data.value;
    const lowerPumpsOrder =
      (equipmentValue?.lower_pumps_order as string[] | undefined) || [];
    const pumps = lowerPumps.value;

    return filterHousingsByPumpOrder(
      lowerPumpsOrder,
      pumps,
      housingsForm.data.value
    );
  });

  // For lower forms, we need to get y-tool properties from the lower equipment
  // Explicitly pass "lower" stack to ensure downhole y-tool properties are used
  const lowerYToolBypassTubings = computed(() => {
    const yToolProps = getYToolProperties(lowerEquipment.value, "lower");
    // Map the array to a record by id, filtering out null ids
    const mapped: Record<
      string,
      XrefYToolBypassTubings[number] | XrefYToolEquipmentTubings[number]
    > = {};
    yToolProps.yToolBypassTubings.forEach(tubing => {
      if (tubing.id) {
        mapped[tubing.id] = tubing as XrefYToolBypassTubings[number];
      }
    });
    return mapped;
  });
  const lowerYToolEquipmentTubings = computed(() => {
    const yToolProps = getYToolProperties(lowerEquipment.value, "lower");
    // Map the array to a record by id, filtering out null ids
    const mapped: Record<
      string,
      XrefYToolBypassTubings[number] | XrefYToolEquipmentTubings[number]
    > = {};
    yToolProps.yToolEquipmentTubings.forEach(tubing => {
      if (tubing.id) {
        mapped[tubing.id] = tubing as XrefYToolEquipmentTubings[number];
      }
    });
    return mapped;
  });

  const lowerForms = computed<DestructuredScenario>(() => ({
    scenario: scenarioForm.data,
    correlations: correlationsForm.data,
    equipment: lowerEquipment as Ref<Equipment | null | undefined>,
    parameters: lowerParameters as Ref<OperatingParameters | null | undefined>,
    wellProperties: lowerWellProperties as Ref<
      WellProperties | null | undefined
    >,
    deviationSurvey: deviationSurveyForm.data,
    seals: lowerSeals as Ref<Record<string, XrefSeals[number]>>,
    intakes: lowerIntakes as Ref<Record<string, XrefIntakes[number]>>,
    cables: lowerCables as Ref<Record<string, XrefCables[number]>>,
    pumps: lowerPumps as Ref<Record<string, XrefPumps[number]>>,
    housings: lowerHousings as Ref<Record<string, XrefHousings[number]>>,
    yToolBypassTubings: lowerYToolBypassTubings as Ref<
      Record<string, XrefYToolBypassTubings[number]>
    >,
    yToolEquipmentTubings: lowerYToolEquipmentTubings as Ref<
      Record<string, XrefYToolEquipmentTubings[number]>
    >
  }));

  // Stack-specific calculations (created after forms are defined)
  // Wrap in computed so calculations recalculate when forms change
  // Pass unfiltered forms with upholeStack parameter to handle filtering internally
  // const upperCalculations = computed(() =>
  //   useScenarioCalculations(unfilteredForms.value, undefined, {
  //     upholeStack: true
  //   })
  // );
  // const lowerCalculations = computed(() =>
  //   useScenarioCalculations(unfilteredForms.value, undefined, {
  //     upholeStack: false
  //   })
  // );

  // Function that returns the appropriate calculations for the given stack
  // const getStackCalculations = (stack: "upper" | "lower" = "upper") => {
  //   return stack === "lower"
  //     ? lowerCalculations.value
  //     : upperCalculations.value;
  // };

  provide(scenarioKey, scenarioForm.data);
  provide(scenarioFormKey, scenarioForm);

  provide(equipmentKey, equipmentForm.data);
  provide(equipmentFormKey, equipmentForm);
  provide(correlationsKey, correlationsForm.data);
  provide(correlationsFormKey, correlationsForm);
  provide(parametersKey, parametersForm.data);
  provide(parametersFormKey, parametersForm);
  provide(wellPropertiesKey, wellPropertiesForm.data);
  provide(wellPropertiesFormKey, wellPropertiesForm);
  provide(deviationSurveyKey, deviationSurveyForm.data);
  provide(deviationSurveyFormKey, deviationSurveyForm);
  provide(sealsMapKey, sealsForm.data);
  provide(sealsFormKey, sealsForm);
  provide(intakesMapKey, intakesForm.data);
  provide(intakesFormKey, intakesForm);
  provide(cablesMapKey, cablesForm.data);
  provide(cablesFormKey, cablesForm);
  provide(pumpsMapKey, pumpsForm.data);
  provide(pumpsFormKey, pumpsForm);
  provide(housingsMapKey, housingsForm.data);
  provide(housingsFormKey, housingsForm);
  provide(yToolBypassTubingsMapKey, yToolBypassTubingsForm.data);
  provide(yToolBypassTubingsFormKey, yToolBypassTubingsForm);
  provide(yToolEquipmentTubingsMapKey, yToolEquipmentTubingsForm.data);
  provide(yToolEquipmentTubingsFormKey, yToolEquipmentTubingsForm);

  // Helper functions to split generic y-tool forms for database operations
  // These split the combined forms based on which items came from uphole vs downhole sources
  type YToolTubingItem =
    | XrefYToolBypassTubings[number]
    | XrefYToolEquipmentTubings[number];

  interface SplitYToolTubingsConfig {
    upholeInitialKey: string;
    downholeInitialKey: string;
    upholeOrderKey: string;
    downholeOrderKey: string;
  }

  /**
   * Generic helper to split y-tool tubings (bypass or equipment) into uphole/downhole
   * Routes items based on: initial data IDs → order arrays → equipment flags
   */
  const splitYToolTubings = <T extends YToolTubingItem>(
    genericForm: Record<string, T>,
    config: SplitYToolTubingsConfig
  ): { uphole: Record<string, T>; downhole: Record<string, T> } => {
    // Get initial IDs from equipment (existing items from database)
    const upholeInitialData = equipment.value?.[
      config.upholeInitialKey as keyof typeof equipment.value
    ] as Array<{ id?: string }> | undefined;
    const upholeIds = new Set(
      (upholeInitialData || [])
        .map(item => item.id)
        .filter((id): id is string => Boolean(id))
    );

    const downholeInitialData = equipment.value?.[
      config.downholeInitialKey as keyof typeof equipment.value
    ] as Array<{ id?: string }> | undefined;
    const downholeIds = new Set(
      (downholeInitialData || [])
        .map(item => item.id)
        .filter((id): id is string => Boolean(id))
    );

    // Get order arrays from current form state (not initial data)
    const equipmentData = equipmentForm.data.value;
    const upholeOrder = new Set(
      (equipmentData?.[config.upholeOrderKey as keyof typeof equipmentData] as
        | string[]
        | undefined) || []
    );
    const downholeOrder = new Set(
      (equipmentData?.[
        config.downholeOrderKey as keyof typeof equipmentData
      ] as string[] | undefined) || []
    );

    const uphole: Record<string, T> = {};
    const downhole: Record<string, T> = {};

    for (const [id, item] of Object.entries(genericForm)) {
      if (upholeIds.has(id)) {
        uphole[id] = item;
      } else if (downholeIds.has(id)) {
        downhole[id] = item;
      } else {
        // New item: determine which table based on order arrays first,
        // then fall back to equipment flags if not in either order array
        if (downholeOrder.has(id)) {
          downhole[id] = item;
        } else if (upholeOrder.has(id)) {
          uphole[id] = item;
        } else if (equipment.value?.use_uphole_y_tool) {
          uphole[id] = item;
        } else if (equipment.value?.use_downhole_y_tool) {
          downhole[id] = item;
        }
      }
    }

    return { uphole, downhole };
  };

  const splitYToolBypassTubings = (
    genericForm: Record<string, XrefYToolBypassTubings[number]>
  ) => {
    return splitYToolTubings(genericForm, {
      upholeInitialKey: "r_xref_uphole_y_tool_bypass_tubings",
      downholeInitialKey: "r_xref_downhole_y_tool_bypass_tubings",
      upholeOrderKey: "uphole_y_tool_bypass_tubing_order",
      downholeOrderKey: "downhole_y_tool_bypass_tubing_order"
    });
  };

  const splitYToolEquipmentTubings = (
    genericForm: Record<string, XrefYToolEquipmentTubings[number]>
  ) => {
    return splitYToolTubings(genericForm, {
      upholeInitialKey: "r_xref_uphole_y_tool_equipment_tubings",
      downholeInitialKey: "r_xref_downhole_y_tool_equipment_tubings",
      upholeOrderKey: "uphole_y_tool_equipment_tubing_order",
      downholeOrderKey: "downhole_y_tool_equipment_tubing_order"
    });
  };

  // Helper functions to prepare split diffs for mutations
  // Uses the same transformation logic as useForm's prepareDiffsAsArray
  const transformYToolItem = (
    item: XrefYToolBypassTubings[number] | XrefYToolEquipmentTubings[number]
  ) => {
    if ("winning_revision_id" in item) {
      const flatData = omit(
        item,
        (value, key) =>
          (key as string).startsWith("r_") ||
          [
            "__typename",
            "winning_revision_id",
            "winning_revision_author_id",
            "parent_id",
            "journal_author_id",
            "journal_created_at",
            "journal_updated_at",
            "journal_deleted_at",
            "winning_revision_created_at",
            "winning_revision_updated_at",
            "winning_revision_deleted_at",
            "winning_revision_parent_id"
          ].includes(key as string)
      );
      return {
        ...flatData,
        id: v4(),
        journal_id: item.id || v4(),
        parent_revision_id: item.winning_revision_id,
        deleted_at: item.journal_deleted_at
      };
    }
    return omit(
      item,
      (v, k) =>
        (k as string).startsWith("r_") || ["__typename"].includes(k as string)
    );
  };

  const prepareSplitYToolBypassTubingsDiffs = () => {
    const split = splitYToolBypassTubings(yToolBypassTubingsForm.data.value);
    const diffKeys = Object.keys(yToolBypassTubingsForm.diff.value);

    const upholeDiffs = Object.keys(split.uphole)
      .filter(key => diffKeys.includes(key))
      .map(key => transformYToolItem(split.uphole[key]));

    const downholeDiffs = Object.keys(split.downhole)
      .filter(key => diffKeys.includes(key))
      .map(key => transformYToolItem(split.downhole[key]));

    return { uphole: upholeDiffs, downhole: downholeDiffs };
  };

  const prepareSplitYToolEquipmentTubingsDiffs = () => {
    const split = splitYToolEquipmentTubings(
      yToolEquipmentTubingsForm.data.value
    );
    const diffKeys = Object.keys(yToolEquipmentTubingsForm.diff.value);

    const upholeDiffs = Object.keys(split.uphole)
      .filter(key => diffKeys.includes(key))
      .map(key => transformYToolItem(split.uphole[key]));

    const downholeDiffs = Object.keys(split.downhole)
      .filter(key => diffKeys.includes(key))
      .map(key => transformYToolItem(split.downhole[key]));

    return { uphole: upholeDiffs, downhole: downholeDiffs };
  };

  provide(scenarioValidatorKey, scenarioValidator);
  provide(scenarioCalculationsKey, scenarioCalculations);
  // provide(stackCalculationsKey, getStackCalculations);
  provide(upperFormsKey, upperForms);
  provide(lowerFormsKey, lowerForms);

  return {
    scenarioForm,
    equipmentForm,
    parametersForm,
    wellPropertiesForm,
    deviationSurveyForm,
    sealsForm,
    intakesForm,
    cablesForm,
    pumpsForm,
    housingsForm,
    yToolBypassTubingsForm,
    yToolEquipmentTubingsForm,
    // Helper functions to split generic forms for database operations
    splitYToolBypassTubings,
    splitYToolEquipmentTubings,
    prepareSplitYToolBypassTubingsDiffs,
    prepareSplitYToolEquipmentTubingsDiffs,
    correlationsForm,
    scenarioCalculations,
    scenarioValidator
  };
};
